import { useCallback, useEffect, useState } from 'react';
import { postJSON } from 'api/fetch';
import Box from 'fe-design-base/atoms/Box';
import Button from 'fe-design-base/molecules/Button';
import NumberAsStringField from 'fe-design-base/molecules/NumberAsStringField';
import Toast from 'fe-design-base/molecules/Toast';
import { Form, Formik, FormikHelpers } from 'formik';

import CountdownButton from 'features/authentication/CountdownButton';
import useResendCountdown from 'features/authentication/hooks/useResendCountdown';
import {
  SIGN_IN_PATH,
  TOO_MANY_REQUESTS_ERROR,
} from 'features/authentication/SignIn/constants';
import HeaderLayout from 'features/authentication/SignIn/HeaderLayout';
import {
  credentialErrorCategory,
  mapAuthenticateCodeErrorToMessage,
} from 'features/authentication/SignIn/helpers';
import { ErrorTrackingProps } from 'features/authentication/SignIn/types';
import { codeValidationSchema } from 'features/authentication/SignIn/validation';

import { toI18n } from 'util/i18n';
import { OTP_EXPIRATION_MINUTES, useStytch } from 'util/stytch';

interface FormValues {
  code: string;
}

type Props = {
  phoneNumber?: string;
  email?: string;
  methodId: string;
  initialAlertMessage?: string;
  onBackClick?: () => void;
  onSetMethodId: (id: string) => void;
  onSuccess: () => void;
  onResendCode: () => Promise<any>;
  bottomContent?: React.ReactNode;
} & ErrorTrackingProps;

const CodeForm = ({
  phoneNumber = '',
  email,
  methodId,
  initialAlertMessage,
  onBackClick,
  onSetMethodId,
  onSuccess,
  onResendCode,
  onError,
  bottomContent,
}: Props) => {
  const stytchClient = useStytch();
  const [toastOpen, setToastOpen] = useState(false);
  const [alertError, setAlertError] = useState('');
  const [isResendLoading, setIsResendLoading] = useState(false);
  const { timeIsActive, tryAgain, buttonText } = useResendCountdown();

  useEffect(() => {
    if (initialAlertMessage) setAlertError(initialAlertMessage);
  }, [initialAlertMessage]);

  const handleError = useCallback(
    // TODO: https://joinhomebase.atlassian.net/browse/FE-2199
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    error => {
      const errorMessage = mapAuthenticateCodeErrorToMessage(error);

      setAlertError(errorMessage);
      onError(errorMessage);
    },
    [onError]
  );

  const handleSubmit = useCallback(
    ({ code }: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      if (alertError) setAlertError('');

      stytchClient
        .authenticateOtp(code, methodId)
        .then(res => {
          if (res.user?.phone_numbers) {
            const verifiedPhoneNumbers = res.user?.phone_numbers.filter(
              p => p.verified
            );
            if (verifiedPhoneNumbers?.length > 1) {
              return postJSON('/authentication/users/remove_unused_factors');
            }
          }
        })
        .then(onSuccess)
        .catch(e => {
          handleError(e);
          setSubmitting(false);
        });
    },
    [alertError, stytchClient, methodId, onSuccess, handleError]
  );

  const handleResendError = useCallback(
    // TODO: https://joinhomebase.atlassian.net/browse/FE-2199
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ({ error }) => {
      const errorType = credentialErrorCategory(error);
      let errorMessage = toI18n('fe_design_base.errors.general');

      if (errorType === TOO_MANY_REQUESTS_ERROR) {
        errorMessage = toI18n('authentication.errors.too_many_requests');
      }

      setAlertError(errorMessage);
      onError(errorMessage);
    },
    [onError]
  );

  const handleCloseToast = useCallback(() => {
    setToastOpen(false);
  }, []);

  const handleResend = useCallback(() => {
    setIsResendLoading(true);
    if (alertError) setAlertError('');

    onResendCode()
      .then(({ phone_id, email_id, method_id }) => {
        // Note: Resend code action can either be Stytch sendMfaSmsOtp endpoint for MFA
        // (returns method_id) or HB sendSmsOtp (phone sign in) or sendEmailOtp (forgot password)
        onSetMethodId(phone_id || email_id || method_id);
        setToastOpen(true);
        tryAgain();
      })
      .catch(error => handleResendError({ error }))
      .finally(() => {
        setIsResendLoading(false);
      });
  }, [alertError, handleResendError, onResendCode, onSetMethodId, tryAgain]);

  return (
    <HeaderLayout
      backLinkTo={onBackClick ? undefined : SIGN_IN_PATH}
      onBackClick={onBackClick}
      title="authentication.code.title"
      description={`authentication.code.${email ? 'email' : 'sms'}_description`}
      descriptionI18nProps={{ phoneLastFour: phoneNumber.slice(-4) }}
      dataTestId="CodeForm"
      alertError={alertError}
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={{ code: '' }}
        validationSchema={codeValidationSchema}
      >
        {({ dirty, isValid, isSubmitting }) => (
          <Form role="form">
            <Box column gap={32} mt={16}>
              <NumberAsStringField
                name="code"
                label={toI18n('authentication.code.field_label')}
                helperText={toI18n('authentication.code.field_helper', {
                  props: { minutes: OTP_EXPIRATION_MINUTES },
                })}
                fullWidth
                dataTestId="CodeForm__code-input"
              />
              <Box column gap={16}>
                <Button
                  type="submit"
                  uxElement="submit code button"
                  disabled={!dirty || !isValid}
                  isLoading={isSubmitting}
                  dataTestId="CodeForm__submit-button"
                >
                  {toI18n('authentication.code.submit_button')}
                </Button>
                <CountdownButton
                  buttonText={buttonText}
                  timeIsActive={timeIsActive}
                  onResendCode={handleResend}
                  dataTestId="CodeForm__resend-button"
                  uxElement="resend code button"
                  isLoading={isResendLoading}
                />
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
      {bottomContent || null}
      <Toast variant="success" open={toastOpen} onClose={handleCloseToast}>
        {toI18n('authentication.code.resend_success')}
      </Toast>
    </HeaderLayout>
  );
};

export default CodeForm;
